import './alteration-card.scss'

import _ from 'lodash'
import { defineComponent, PropType } from 'vue'
import { faPen, faEllipsisH, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons'

import { Alteration, ThesaurusTerm, createTranslatedString, editTranslatedString, getTranslatedString } from '@skinsoft/types'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { reactiveVueMixin } from "@skinsoft/reactive-vue"
import { Media } from '@skinsoft/ui/src/item-card/media'
import ThesaurusTermTreeModal, { THESAURUS_TERM_SELECTED } from '@skinsoft/ui/src/thesaurus-term-tree/thesaurus-term-tree-modal'
import ThesaurusTermTree from '@skinsoft/ui/src/thesaurus-term-tree'
import { AutoCompleteTextField2, AutoCompleteTextField2Modal, FIELD_VALUE_UPDATED } from '@skinsoft/ui2'

export const ALTERATION_CARD_DELETE_ALTERATION = 'alteration-card-delete-alteration'
export const ALTERATION_CARD_ALTERATION_UPDATED = 'alteration-card-alteration-type-updated'

export enum MainAlterationCardField {
    Title,
    AlterationType,
}

export interface AlterationChanged {
    index: number,
    updates: Partial<Alteration>
}

export type AlterationWithThesaurusTerm = Alteration & { typeOfAlterationObject: ThesaurusTerm | null }

export default defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sMedia: Media,
        sThesaurusTermTreeModal: ThesaurusTermTreeModal,
        sThesaurusTermTree: ThesaurusTermTree,
        sAutoCompleteTextField2: AutoCompleteTextField2,
        sAutoCompleteTextField2Modal: AutoCompleteTextField2Modal,
    },
    props: {
        type: Array,
        alteration: {
            type: Object as PropType<AlterationWithThesaurusTerm>,
            required: true,
        },
        index: {
            type: Number,
        },
        mainField: {
            default: MainAlterationCardField.Title
        },
        readOnly: {
            type: Boolean
        },
        existingTitles: {
            type: Array as PropType<String[]>,
            default:() => []
        },
    },
    template: require('./alteration-card.html'),
    data() {
        return {
            // @ts-ignore
            updatedAlteration: {} as AlterationWithThesaurusTerm,
            isEditing: false,
            moreMenuOpen: false,
            isDeleteConfirmationOpen: false,

            editIcon: faPen,
            moreIcon: faEllipsisH,
            cancelIcon: faTimes,
            validateIcon: faCheck,

            THESAURUS_TERM_SELECTED: THESAURUS_TERM_SELECTED,
            FIELD_VALUE_UPDATED: FIELD_VALUE_UPDATED,

            MAIN_FIELD_TITLE: MainAlterationCardField.Title,
            MAIN_FIELD_ALTERATION_TYPE: MainAlterationCardField.AlterationType,
        }
    },
    mounted() {
        this.updatedAlteration = _.cloneDeep(this.alteration)
    },
    computed: {
        mainFieldValue(): string {
            switch(this.mainField) {
                case MainAlterationCardField.Title:
                    return getTranslatedString(this.updatedAlteration.title, this.getAppLocale())
                case MainAlterationCardField.AlterationType:
                    if(this.updatedAlteration.typeOfAlterationObject) {
                        return getTranslatedString(
                            thesaurusTermSelectors.preferredTerm(this.updatedAlteration.typeOfAlterationObject),
                            this.getAppLocale()
                        )
                    }
                    break
            }

            return ''
        },

        updatedAlterationTitle: {
            get(): string {
                return getTranslatedString(this.updatedAlteration.title, this.getAppLocale())
            },
            set(value: string) {
                if(this.updatedAlteration.title) {
                    editTranslatedString(this.updatedAlteration.title, value, this.getAppLocale())
                } else {
                    _.set(this.updatedAlteration, 'title', createTranslatedString(this.getAppLocale(), value))
                }
            },
        },

        updatedAlterationPrecision: {
            get(): string {
                return getTranslatedString(this.updatedAlteration.precision, this.getAppLocale())
            },
            set(value: string) {
                if(this.updatedAlteration.precision) {
                    editTranslatedString(this.updatedAlteration.precision, value, this.getAppLocale())
                } else {
                    _.set(this.updatedAlteration, 'precision', createTranslatedString(this.getAppLocale(), value))
                }
            },
        },
    },
    methods: {
        onEditingCanceled() {
            this.updatedAlteration = _.cloneDeep(this.alteration)
            this.toggleEdit()
        },

        openThesaurusTermModal() {
            (this.$refs.alterationTypeModal as InstanceType<typeof ThesaurusTermTreeModal>).openModal()
            this.moreMenuOpen = false
        },

        openTitleModal() {
            (this.$refs.alterationTitleModal as InstanceType<typeof AutoCompleteTextField2Modal>).openModal()
            this.moreMenuOpen = false
        },

        onTypeOfAlterationChanged(thesaurusTermId: string) {
            this.updatedAlteration.typeOfAlteration = thesaurusTermId
            this.$thesaurusTermDataStore.fetchOne(thesaurusTermId)
                .subscribe(thesaurusTerm => {
                    if(thesaurusTerm) {
                        this.updatedAlteration.typeOfAlterationObject = thesaurusTerm
                    }else {
                        this.updatedAlteration.typeOfAlterationObject = null
                    }
                })
        },

        updateAlteration() {
            let updates = _.cloneDeep(this.updatedAlteration)
            // @ts-ignore
            delete updates.typeOfAlterationObject
            // @ts-ignore
            delete this.updatedAlteration.index

            this.$emit(ALTERATION_CARD_ALTERATION_UPDATED, { index: this.index, updates: updates } as AlterationChanged)
            this.toggleEdit()
        },

        openDeleteConfirmation() {
            if(!this.readOnly) {
                this.isDeleteConfirmationOpen = true
            }
        },

        deleteAlteration() {
            this.$emit(ALTERATION_CARD_DELETE_ALTERATION, this.index)
        },

        toggleEdit() {
            if(!this.readOnly) {
                this.isEditing = !this.isEditing
                this.moreMenuOpen = false
            }
        },

        toggleMoreMenu() {
            if(!this.readOnly) {
                this.moreMenuOpen = !this.moreMenuOpen
                this.isDeleteConfirmationOpen = false
            }
        },

        onTitleUpdated(value: String) {
            this.updatedAlterationTitle = value as string
        },
    },
    watch: {
        alteration() {
            this.updatedAlteration = _.cloneDeep(this.alteration)
            this.moreMenuOpen = false
            this.isEditing = false
        },
    },
})
